/**
 * SignInModal — anon Profile-tap explainer + sign-in CTAs.
 * Extracted from App.jsx (2026-07-06) to its own file to match the
 * SignUpPrompt / EmailReentryModal pattern and enable isolated tests.
 * Shown when an anonymous user taps Profile before signing in, so their
 * puzzles can be kept safe/synced. Offers Google sign-in plus an email
 * magic-link fallback for anyone who'd rather not use Google. On iOS also
 * Sign in with Apple (#31): `onAppleSignIn` is null where it isn't offered.
 */
import React, { useState, useEffect } from "react";
import { useTranslation, Trans } from "react-i18next";
import { sendMagicLink, friendlyAuthError } from "../services/authService";
import AppleSignInButton from "./AppleSignInButton";

export default function SignInModal({ open, busy, onClose, onSignIn, onAppleSignIn = null, dark = false, error = "", onResolveAppleConflict = null, onLegal = null, onRecommend = null }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [linkState, setLinkState] = useState("idle"); // idle | sending | sent
  const [linkError, setLinkError] = useState("");

  useEffect(() => {
    if (!open) {
      setEmail("");
      setLinkState("idle");
      setLinkError("");
    }
  }, [open]);

  if (!open) return null;

  const handleSendLink = async () => {
    if (!email || !/.+@.+\..+/.test(email)) {
      setLinkError(t("ui.signIn.invalidEmail"));
      return;
    }
    setLinkState("sending");
    setLinkError("");
    try {
      await sendMagicLink(email.trim());
      setLinkState("sent");
    } catch (err) {
      setLinkError(friendlyAuthError(err));
      setLinkState("idle");
    }
  };

  const resetLink = () => { setLinkState("idle"); setLinkError(""); };

  const overlayStyle = {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.5)",
    backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
    zIndex: 340,
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: 24,
  };
  const cardStyle = {
    background: "var(--color-surface)",
    borderRadius: "2rem", padding: 32,
    maxWidth: 420, width: "100%",
    boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
    position: "relative", textAlign: "center",
  };
  const ctaPrimary = {
    width: "100%", padding: "14px 20px",
    border: "none", borderRadius: 16,
    fontFamily: "var(--font-body)", fontSize: 16, fontWeight: 700,
    cursor: busy ? "default" : "pointer",
    opacity: busy ? 0.7 : 1,
    background: "#ffffff", color: "#3c4043",
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
  };
  const ctaSecondary = {
    width: "100%", padding: "14px 20px",
    border: "none", borderRadius: 16,
    fontFamily: "var(--font-body)", fontSize: 16, fontWeight: 700,
    cursor: "pointer",
    background: "var(--color-bgAlt)", color: "var(--color-text)",
    boxShadow: "none",
  };
  const dividerRow = {
    display: "flex", alignItems: "center", gap: 12, margin: "20px 0 16px",
    color: "var(--color-textMuted)", fontFamily: "var(--font-body)", fontSize: 13,
  };
  const dividerLine = { flex: 1, height: 2, background: "var(--color-bgAlt)", borderRadius: 2 };
  const emailInput = {
    width: "100%", padding: "14px 16px", border: "none", borderRadius: 12,
    background: "var(--color-bgAlt)", fontFamily: "var(--font-body)", fontSize: 16,
    color: "var(--color-text)", boxSizing: "border-box", marginBottom: 10,
  };
  const linkCta = {
    ...ctaSecondary, background: "var(--color-bgAlt)", color: "var(--color-text)",
  };
  const resetLinkStyle = {
    display: "block", margin: "12px auto 0", background: "none", border: "none",
    color: "var(--color-textMuted)", fontFamily: "var(--font-body)", fontSize: 13,
    cursor: "pointer", textDecoration: "underline",
  };

  // Inline Google "G" mark (no asset dependency).
  const googleG = (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );

  return (
    <div onClick={busy ? undefined : onClose} style={overlayStyle}>
      <div onClick={(e) => e.stopPropagation()} style={cardStyle} role="dialog" aria-modal="true">
        <div
          style={{
            width: 64, height: 64, borderRadius: 20, margin: "0 auto 16px",
            background: "linear-gradient(135deg, var(--color-primary), var(--color-primaryBright))",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 28, boxShadow: "0 8px 24px var(--color-glow)",
          }}
          aria-hidden="true"
        >
          👤
        </div>
        <h2 style={{
          fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 800,
          color: "var(--color-text)", margin: "0 0 12px",
        }}>
          {t("ui.signIn.title")}
        </h2>
        <p style={{
          fontFamily: "var(--font-body)", fontSize: 14, color: "var(--color-textMuted)",
          lineHeight: 1.6, margin: "0 0 24px",
        }}>
          {t("ui.signIn.body")}
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button
            type="button"
            onClick={busy ? undefined : onSignIn}
            disabled={busy}
            style={ctaPrimary}
            data-testid="signin-modal-google"
          >
            {googleG}
            {busy ? t("ui.signIn.googleBusy") : t("ui.signIn.google")}
          </button>
          {onAppleSignIn && <AppleSignInButton onClick={onAppleSignIn} busy={busy} dark={dark} />}
          <button type="button" onClick={onClose} disabled={busy} style={ctaSecondary}>
            {t("ui.signIn.notNow")}
          </button>
        </div>
        {error && (
          <p role="alert" data-testid="signin-modal-error" style={{ fontFamily: "var(--font-body)", fontSize: 13, color: "var(--color-wrong)", textAlign: "center", margin: "12px 0 0" }}>
            {error}
          </p>
        )}
        {onResolveAppleConflict && (
          <button
            type="button"
            onClick={busy ? undefined : onResolveAppleConflict}
            disabled={busy}
            style={{ ...ctaPrimary, marginTop: 12 }}
            data-testid="signin-modal-google-then-apple"
          >
            {googleG}
            {busy ? t("ui.signIn.googleBusy") : t("ui.signIn.googleThenApple")}
          </button>
        )}

        <div style={dividerRow}>
          <span style={dividerLine} /> {t("ui.signIn.or")} <span style={dividerLine} />
        </div>

        {linkState === "sent" ? (
          <div style={{ textAlign: "center" }}>
            <p style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 18, color: "var(--color-text)", margin: "0 0 6px" }}>
              {t("ui.signIn.inboxTitle")}
            </p>
            <p style={{ fontFamily: "var(--font-body)", fontSize: 14, color: "var(--color-textMuted)", margin: 0 }}>
              <Trans i18nKey="ui.signIn.inboxBody" values={{ email }} components={{ strong: <strong /> }} />
            </p>
            <button type="button" style={resetLinkStyle} onClick={resetLink}>
              {t("ui.signIn.useDifferentEmail")}
            </button>
          </div>
        ) : (
          <>
            <input
              type="email"
              aria-label={t("ui.signIn.emailAria")}
              placeholder={t("ui.signIn.emailPlaceholder")}
              maxLength={254}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && linkState !== "sending") handleSendLink(); }}
              style={emailInput}
              disabled={linkState === "sending"}
            />
            <button
              type="button"
              onClick={handleSendLink}
              disabled={linkState === "sending"}
              style={linkCta}
            >
              {linkState === "sending" ? t("ui.signIn.sending") : t("ui.signIn.sendLink")}
            </button>
            {linkError && (
              <p role="alert" style={{ fontFamily: "var(--font-body)", fontSize: 13, color: "var(--color-wrong)", textAlign: "center", margin: "12px 0 0" }}>
                {linkError}
              </p>
            )}
          </>
        )}
        {/* #125: anche chi non ha un account può consigliare l'app. */}
        {onRecommend && (
          <button
            type="button"
            onClick={onRecommend}
            disabled={busy}
            style={{ ...resetLinkStyle, marginTop: 20, color: "var(--color-text)", fontWeight: 600, textDecoration: "none" }}
            data-testid="signin-modal-recommend"
          >
            <span aria-hidden="true">💌 </span>{t("common.account.recommend")}
          </button>
        )}
        {/* #121: chi non ha ancora un account arriva qui dal tab Profilo. */}
        {onLegal && (
          <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 16 }}>
            {["privacy", "terms"].map((pagina) => (
              <button
                key={pagina}
                type="button"
                onClick={() => onLegal(pagina)}
                disabled={busy}
                style={resetLinkStyle}
                data-testid={`signin-modal-${pagina}`}
              >
                {t(`common.account.${pagina}`)}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
